import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { addClient } from '../../firebase/database';
import { Client } from '../../types';
import toast from 'react-hot-toast';

const CreateClient: React.FC = () => {
  const navigate = useNavigate();
  const { currentMode } = useAuth();
  
  // Form state
  const [name, setName] = useState<string>('');
  const [address, setAddress] = useState<string>('');
  const [clientType, setClientType] = useState<'import' | 'export' | 'both'>('import');
  const [openingBalance, setOpeningBalance] = useState<string>('0');
  
  // UI states
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string>('');

  // Check if user is in inputter mode
  if (currentMode !== 'inputter') {
    return (
      <div className="min-h-screen bg-gray-50 rounded-lg flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-md">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h2>
          <p className="text-gray-600 mb-4">Please switch to Inputter mode to create new client accounts.</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // Generate unique ID (numeric only)
  const generateUniqueId = (): string => {
    let result = '';
    for (let i = 0; i < 5; i++) {
      result += Math.floor(Math.random() * 10).toString();
    }
    return result;
  };

  // Validate form
  const validateForm = (): boolean => {
    if (!name.trim()) {
      setError('Client name is required');
      return false;
    }
    if (!address.trim()) {
      setError('Address is required');
      return false;
    }
    const ob = Number(openingBalance);
    if (isNaN(ob) || ob < 0) {
      setError('Opening balance must be a non-negative number');
      return false;
    }
    setError('');
    return true;
  };

  // Submit client
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const client: Omit<Client, 'id' | 'createdAt'> = {
        name: name.trim(),
        uniqueId: generateUniqueId(),
        address: address.trim(),
        clientType,
        openingBalance: Number(openingBalance) || 0,
        outstanding: 0
      };

      await addClient(client);
      toast.success('Client account created successfully!');
      navigate('/dashboard');
    } catch (error) {
      setError('Error creating client account. Please try again.');
      console.error('Client creation error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen relative py-8">
      {/* Background Image with Blur */}
      <div 
        className="fixed inset-0 w-full h-full bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: 'url(/assets/background.png)',
          filter: 'blur(0px)',
          transform: 'scale(1.1)'
        }}
      />
      
      {/* Overlay to darken the background */}
      <div className="fixed inset-0 bg-gray-900 bg-opacity-30" />

      {/* Content */}
      <div className="relative z-10 max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-white drop-shadow-lg">Create New Client Account</h1>
              <p className="mt-2 text-white/90 drop-shadow-md">Add a new client account to the system</p>
            </div>
            <button
              onClick={() => navigate('/dashboard')}
              className="px-4 py-2 text-sm font-medium text-white bg-white/50 backdrop-blur-md border border-white/30 rounded-lg hover:bg-white/50 transition-all duration-200"
            >
              Back to Dashboard
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-6 bg-red-500/20 backdrop-blur-md border-2 border-red-400/50 text-white px-4 py-3 rounded-xl">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="bg-white/50 backdrop-blur-lg shadow-2xl rounded-2xl p-8 border-2 border-white/20">
          <div className="space-y-6">
            {/* Client Name */}
            <div>
              <label className="block text-sm font-semibold text-white mb-2 drop-shadow-md">
                Client Name *
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-4 py-2.5 bg-white/50 backdrop-blur-sm border-2 border-white/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 text-gray-900 placeholder-gray-500"
                placeholder="Enter client name"
                required
              />
            </div>

            {/* Address */}
            <div>
              <label className="block text-sm font-semibold text-white mb-2 drop-shadow-md">
                Address *
              </label>
              <textarea
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                rows={3}
                className="w-full px-4 py-2.5 bg-white/50 backdrop-blur-sm border-2 border-white/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 text-gray-900 placeholder-gray-500"
                placeholder="Enter complete address"
                required
              />
            </div>

            {/* Client Type */}
            <div>
              <label className="block text-sm font-semibold text-white mb-2 drop-shadow-md">
                Client Type *
              </label>
              <select
                value={clientType}
                onChange={(e) => setClientType(e.target.value as 'import' | 'export' | 'both')}
                className="w-full px-4 py-2.5 bg-white/50 backdrop-blur-sm border-2 border-white/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 text-gray-900"
                required
              >
                <option value="import">Import</option>
                <option value="export">Export</option>
                <option value="both">Both</option>
              </select>
            </div>

            {/* Opening Balance */}
            <div>
              <label className="block text-sm font-semibold text-white mb-2 drop-shadow-md">
                Opening Balance
              </label>
              <input
                type="number"
                value={openingBalance}
                onChange={(e) => setOpeningBalance(e.target.value)}
                min="0"
                step="0.01"
                className="w-full px-4 py-2.5 bg-white/50 backdrop-blur-sm border-2 border-white/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 text-gray-900 placeholder-gray-500"
                placeholder="0.00"
              />
            </div>

            {/* Submit Button */}
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-6 py-3 text-sm font-semibold text-white bg-indigo-600/90 backdrop-blur-sm border-2 border-indigo-500/50 rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 transition-all duration-200 shadow-lg"
              >
                {isSubmitting ? 'Creating Client Account...' : 'Create Client Account'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateClient;
